import React from 'react';
import _ from 'underscore';

/**
 * A hook that returns a ref and a value indicating whether the mouse is hovering the element
 * referenced by the ref.
 */
export const useHover = <T extends Element>() => {
  const ref = React.useRef<T>(null);
  const [hovering, setHovering] = React.useState(false);

  // Handler for mouse movement. Throttled each 30 ms to rate limit all mouse move events.
  const onMouseMove = React.useMemo(
    () =>
      _.throttle((e: MouseEvent) => {
        const rect = ref.current?.getBoundingClientRect();

        // For some reason the mouse position is outside the getBoundingClientRect by one px approx.
        // Don't know why, as this code runs only when mouse enters the element and the mouse
        // should be within it.
        //
        // To fix it, use a padding to include this.
        // This will result in two icons being hovered if they are rendered close to each other.
        const padding = 1;

        if (rect) {
          const mouseOutside =
            e.clientX < rect.left - padding ||
            e.clientX > rect.right + padding ||
            e.clientY < rect.top - padding ||
            e.clientY > rect.bottom + padding;

          // When mouse goes outside element, also remove the mouse move event listener.
          if (mouseOutside) {
            setHovering(false);
            window.removeEventListener('mousemove', onMouseMove);
          }
        }
      }, 30),
    [],
  );

  React.useEffect(() => {
    // As soon as mouse enters the element, add an event listener for mouse movement in order to
    // detect when mouse leaves the element. Using the mouseleave event is not reliable and doesn't
    // work all the time.
    const onMouseEnter = () => {
      setHovering(true);
      window.addEventListener('mousemove', onMouseMove);
    };

    const refValue = ref.current;
    refValue?.addEventListener('mouseenter', onMouseEnter);

    return () => {
      refValue?.removeEventListener('mouseenter', onMouseEnter);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [onMouseMove]);

  return {
    ref,
    hovering,
  };
};
